import type { ReactNode } from 'react';
import { useLocation } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { RiLockLine, RiToolsLine, type RemixiconComponentType } from '@remixicon/react';
import { cn } from '@/shared/lib/cn';
import { NAV_GROUPS, splitTenantPath } from './nav';
import { PageHeader } from './PageHeader';
import { ContentPanel, PageLayout } from './PageLayout';

/**
 * Product empty state with the glow (01-design-system §4.16, 256 variant of 351:8127): three blurred 96 px circles
 * (#05b4ff, #0085ff, #35b5ec at 30 %), the glyph drawn as a 1 px White/20 outline, title 600 18 and a 500 13.5 hint
 * in White/60.
 */
export function GlowEmptyState({
  icon: Icon,
  title,
  hint,
  children,
  className,
}: {
  icon: RemixiconComponentType;
  title: ReactNode;
  hint?: ReactNode;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex w-420 max-w-full flex-col items-center gap-16 text-center', className)}>
      <div aria-hidden className="relative size-256 shrink-0">
        <span className="absolute top-112 left-104 size-96 rounded-full bg-other-05b4ff opacity-30 blur-glow" />
        <span className="absolute top-112 left-56 size-96 rounded-full bg-other-0085ff opacity-30 blur-glow" />
        <span className="absolute top-48 left-80 size-96 rounded-full bg-blue-1000 opacity-30 blur-glow" />
        {/* The component's fill-less "Blur" rect (background blur 60) renders nothing in Figma, so it is omitted.
            Remix glyphs are filled shapes; the design strokes their outline (24-unit viewBox → 0.25 = 1 px at 96). */}
        <Icon color="none" stroke="currentColor" strokeWidth={0.25} className="absolute top-80 left-80 size-96 text-white-200" />
      </div>
      <p className="text-lg leading-auto font-semibold tracking-normal text-fg">{title}</p>
      {hint ? <p className="text-[0.84375rem] leading-auto font-medium text-fg-secondary">{hint}</p> : null}
      {children}
    </div>
  );
}

/** Glyph of the sidebar page at `pathname`, if any. */
function leafIcon(pathname: string): RemixiconComponentType | undefined {
  const rest = splitTenantPath(pathname)?.rest;
  if (rest == null) return undefined;
  for (const g of NAV_GROUPS) for (const l of g.leaves) if (rest === l.segment || rest.startsWith(l.segment + '/')) return l.icon;
  return undefined;
}

/**
 * Placeholder page for routes whose feature hasn't landed yet: sets the page header and shows the glowing empty
 * state in a content panel.
 *
 *   export const Route = createFileRoute('/_authed/t/$tenantSlug/_shell/employees')({
 *     component: () => <ComingSoon title={t('pages.employees')} />,
 *   });
 */
export function ComingSoon({
  title,
  hint,
  icon,
  header = true,
}: {
  title: string;
  hint?: string;
  icon?: RemixiconComponentType;
  /** set the page header title too (default) */
  header?: boolean;
}) {
  const { t } = useTranslation('shell');
  const pathname = useLocation({ select: (l) => l.pathname });
  return (
    <>
      {header ? <PageHeader title={title} /> : null}
      <PageLayout>
        <ContentPanel className="items-center justify-center">
          <GlowEmptyState icon={icon ?? leafIcon(pathname) ?? RiToolsLine} title={title} hint={hint ?? t('comingSoon.hint')} />
        </ContentPanel>
      </PageLayout>
    </>
  );
}

/** "Ruxsat yo'q" (03-modules §2.6): the page belongs to a module the user has no right to see. */
export function NoAccess() {
  const { t } = useTranslation('shell');
  return (
    <>
      <PageHeader title={t('noAccess.title')} />
      <PageLayout>
        <ContentPanel className="items-center justify-center">
          <GlowEmptyState icon={RiLockLine} title={t('noAccess.title')} hint={t('noAccess.hint')} />
        </ContentPanel>
      </PageLayout>
    </>
  );
}
